/**
 * ReservationDataContext.tsx
 *
 * 予約・車両・車種データをアプリ全体で共有するContext。
 * reservationSyncService / vehicleSyncService のキャッシュを React state に橋渡しし、
 * ページ遷移時に再フェッチが発生しないようにする。
 *
 * 提供するデータ範囲:
 *   - reservations : 14日前〜90日後（SYNC_PAST_DAYS / SYNC_FUTURE_DAYS）
 *   - vehicles     : 全件
 *   - vehicleTypes : 全件
 */

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, ReactNode } from 'react';
import { reservationSyncService } from '../services/reservationSyncService';
import { vehicleSyncService } from '../services/vehicleSyncService';
import { ensureCarTypesLoaded, CAR_TYPES } from '../data/carTypes';
import type { Vehicle, VehicleType, Reservation } from '../types';

type VehiclesMap = Record<string, Vehicle>;

interface ReservationDataContextValue {
  reservations: Reservation[];
  vehicles: VehiclesMap;
  vehicleTypes: VehicleType[];
  isReady: boolean;
  isLoadingReservations: boolean;
  isLoadingVehicles: boolean;
  isLoadingVehicleTypes: boolean;
  getVehiclesList: () => Vehicle[];
}

interface ReservationDataProviderProps {
  children: ReactNode;
}

const ReservationDataContext = createContext<ReservationDataContextValue | null>(null);

export function ReservationDataProvider({ children }: ReservationDataProviderProps) {
  // ---- State ----
  const [reservations, setReservations] = useState<Reservation[]>(() => reservationSyncService.getReservations());
  const [vehicles, setVehicles] = useState<VehiclesMap>(() => {
    const list = vehicleSyncService.getVehicles();
    if (Array.isArray(list) && list.length > 0) {
      const map: VehiclesMap = {};
      list.forEach((v: any) => { map[String(v.id)] = v; });
      return map;
    }
    return {};
  });
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>(() =>
    Array.isArray(CAR_TYPES.list) && CAR_TYPES.list.length > 0 ? CAR_TYPES.list : []
  );
  const [isLoadingReservations, setIsLoadingReservations] = useState<boolean>(
    () => !reservationSyncService.hasData()
  );
  const [isLoadingVehicles, setIsLoadingVehicles] = useState<boolean>(
    () => !vehicleSyncService.hasData()
  );
  const [isLoadingVehicleTypes, setIsLoadingVehicleTypes] = useState<boolean>(
    () => !(Array.isArray(CAR_TYPES.list) && CAR_TYPES.list.length > 0)
  );

  // ---- 予約データ購読 + サービス起動 ----
  useEffect(() => {
    const handleReservationChanges = (_changes: any, allReservations: any) => {
      setReservations(Array.isArray(allReservations) ? allReservations : []);
      setIsLoadingReservations(false);
    };

    const unsubscribe = reservationSyncService.subscribe(handleReservationChanges);

    // 購読開始時点でキャッシュがあれば即座に反映
    const current = reservationSyncService.getReservations();
    if (current.length > 0) {
      setReservations(current);
      setIsLoadingReservations(false);
    } else {
      // キャッシュがなければサービスを起動（ポーリングなし、初回取得のみ）
      reservationSyncService.startOnce();
    }

    return unsubscribe;
  }, []);

  // ---- 車両データ購読 + サービス起動 ----
  useEffect(() => {
    const handleVehicleChanges = (_changes: any, allVehicles: any) => {
      const arr = Array.isArray(allVehicles) ? allVehicles : [];
      const map: VehiclesMap = {};
      arr.forEach((v: any) => { map[String(v.id)] = v; });
      setVehicles(map);
      setIsLoadingVehicles(false);
    };

    const unsubscribe = vehicleSyncService.subscribe(handleVehicleChanges);

    // 購読開始時点でキャッシュがあれば即座に反映
    const current = vehicleSyncService.getVehicles();
    if (Array.isArray(current) && current.length > 0) {
      const map: VehiclesMap = {};
      current.forEach((v: any) => { map[String(v.id)] = v; });
      setVehicles(map);
      setIsLoadingVehicles(false);
    } else {
      // キャッシュがなければサービスを起動（ポーリングなし、初回取得のみ）
      vehicleSyncService.startOnce();
    }

    return unsubscribe;
  }, []);

  // ---- 車種データ（1回のみ取得）----
  useEffect(() => {
    if (Array.isArray(CAR_TYPES.list) && CAR_TYPES.list.length > 0) {
      setVehicleTypes(CAR_TYPES.list);
      setIsLoadingVehicleTypes(false);
      return;
    }
    ensureCarTypesLoaded()
      .then((container: any) => {
        const list = Array.isArray(container?.list) ? container.list : [];
        setVehicleTypes(list);
      })
      .catch(() => {
        setVehicleTypes(Array.isArray(CAR_TYPES.list) ? CAR_TYPES.list : []);
      })
      .finally(() => {
        setIsLoadingVehicleTypes(false);
      });
  }, []);

  // ---- 全データの準備完了フラグ ----
  const isReady = !isLoadingReservations && !isLoadingVehicles && !isLoadingVehicleTypes;

  // ---- vehicles を配列で取得するヘルパー ----
  const getVehiclesList = useCallback((): Vehicle[] => Object.values(vehicles), [vehicles]);

  // ---- Context 値（useMemo で安定化）----
  const value = useMemo((): ReservationDataContextValue => ({
    reservations,
    vehicles,
    vehicleTypes,
    isReady,
    isLoadingReservations,
    isLoadingVehicles,
    isLoadingVehicleTypes,
    getVehiclesList,
  }), [
    reservations,
    vehicles,
    vehicleTypes,
    isReady,
    isLoadingReservations,
    isLoadingVehicles,
    isLoadingVehicleTypes,
    getVehiclesList,
  ]);

  return (
    <ReservationDataContext.Provider value={value}>
      {children}
    </ReservationDataContext.Provider>
  );
}

/**
 * 予約・車両データContextを使用するフック
 */
export function useReservationData(): ReservationDataContextValue {
  const context = useContext(ReservationDataContext);
  if (!context) {
    throw new Error('useReservationData must be used within a ReservationDataProvider');
  }
  return context;
}
